import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import api from '../api/axios';
import './AddProductModal.css';

const NAMED_COLORS = {
    red: '#FF0000', blue: '#0000FF', green: '#008000', yellow: '#FFD700',
    orange: '#FF8C00', pink: '#FF69B4', purple: '#800080', violet: '#EE82EE',
    white: '#FFFFFF', black: '#000000', grey: '#808080', gray: '#808080',
    brown: '#A52A2A', navy: '#001F5B', maroon: '#800000', teal: '#008080',
    cyan: '#00BFFF', magenta: '#FF00FF', lime: '#32CD32', indigo: '#4B0082',
    gold: '#FFD700', silver: '#C0C0C0', beige: '#F5F5DC', cream: '#FFFDD0',
    coral: '#FF6B6B', turquoise: '#40E0D0', khaki: '#C3B091', olive: '#808000',
};

const resolveColorHex = (name, hex) => {
    if (hex) return hex;
    if (name?.startsWith('#')) return name; // bare hex entered as the name
    return NAMED_COLORS[name?.toLowerCase()] || '#888888';
};

// Matches spring.servlet.multipart.max-file-size in ecom-backend.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const THUMB_STYLE = { width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' };

/** One color's photo assignment. A top-level component (not an inline closure)
 *  so each color can hold its own "show the full library" toggle — without
 *  that, uploading a photo for Red would sit in a pool shown under every
 *  other color too, since they all draw from the same staged/uploaded set. */
const ColorPhotoPicker = ({ colorName, colorHex, assignment, existingImageAttachments, newImagePreviews, onUpload, onSelectExisting, onSelectPending, onClear }) => {
    const [showLibrary, setShowLibrary] = useState(false);
    const colorImageAttachmentId = assignment?.colorImageAttachmentId ?? null;
    const colorNewImageIndex = assignment?.colorNewImageIndex ?? null;
    const hasSelection = colorImageAttachmentId != null || colorNewImageIndex != null;
    const selectedUrl = colorImageAttachmentId != null
        ? existingImageAttachments.find(img => img.id === colorImageAttachmentId)?.url
        : (colorNewImageIndex != null ? newImagePreviews[colorNewImageIndex] : null);
    const libraryCount = existingImageAttachments.length + newImagePreviews.length;

    return (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 12px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: colorHex, display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: '13px', color: '#222' }}>{colorName}</span>
            </div>
            <div style={{ padding: '8px 12px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    <label
                        title="Upload a photo just for this color"
                        style={{
                            width: '44px', height: '44px', borderRadius: '6px', cursor: 'pointer',
                            border: '1.5px dashed #1a73e8', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '20px', lineHeight: 1, color: '#1a73e8',
                            background: '#f0f6ff', flexShrink: 0,
                        }}
                    >
                        +
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { onUpload(e.target.files[0]); e.target.value = ''; }} />
                    </label>
                    <button
                        type="button"
                        onClick={onClear}
                        title="No dedicated photo — falls back to the default image"
                        style={{
                            width: '44px', height: '44px', borderRadius: '6px', cursor: 'pointer',
                            border: !hasSelection ? '2px solid #1a73e8' : '1px solid #ddd',
                            background: '#f5f5f5', fontSize: '10px', color: '#999', flexShrink: 0,
                        }}
                    >
                        None
                    </button>
                    {selectedUrl && (
                        <img src={selectedUrl} alt={colorName} style={{ ...THUMB_STYLE, border: '2px solid #1a73e8', cursor: 'default' }} />
                    )}
                    {libraryCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowLibrary(s => !s)}
                            style={{ background: 'none', border: 'none', color: '#1a73e8', fontSize: '11px', cursor: 'pointer', padding: '4px 2px' }}
                        >
                            {showLibrary ? 'Hide uploaded photos ▲' : `Choose from uploaded (${libraryCount}) ▾`}
                        </button>
                    )}
                </div>
                {showLibrary && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e5e5e5' }}>
                        {existingImageAttachments.map(img => (
                            <img
                                key={`existing-${img.id}`}
                                src={img.url}
                                alt="Product"
                                onClick={() => onSelectExisting(img.id)}
                                title="Use this photo for this color"
                                style={{ ...THUMB_STYLE, border: colorImageAttachmentId === img.id ? '2px solid #1a73e8' : '1px solid #ddd' }}
                            />
                        ))}
                        {newImagePreviews.map((url, idx) => (
                            <div key={`pending-${idx}`} style={{ position: 'relative' }}>
                                <img
                                    src={url}
                                    alt="Newly selected"
                                    onClick={() => onSelectPending(idx)}
                                    title="Use this newly selected photo for this color"
                                    style={{ ...THUMB_STYLE, border: colorNewImageIndex === idx ? '2px solid #1a73e8' : '1px solid #ddd' }}
                                />
                                <span
                                    title="Not uploaded yet — uploads when you save"
                                    style={{
                                        position: 'absolute', bottom: -1, right: -1, background: '#1a73e8', color: '#fff',
                                        fontSize: '8px', fontWeight: 700, borderRadius: '3px', padding: '1px 3px', lineHeight: 1,
                                    }}
                                >
                                    NEW
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const AddProductModal = ({ isOpen, onClose, onProductAdded, productToEdit }) => {
    const { user } = useContext(AuthContext);
    const { addToast } = useToast();
    const confirm = useConfirm();
    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [mrp, setMrp] = useState('');
    const [eyantraPurchasePrice, setEyantraPurchasePrice] = useState('');
    const [marketOperatingPrice, setMarketOperatingPrice] = useState('');
    const [brand, setBrand] = useState('');
    const [gst, setGst] = useState('0');
    const [colors, setColors] = useState('');
    const [sizes, setSizes] = useState('');
    const [dimensionL, setDimensionL] = useState('');
    const [dimensionW, setDimensionW] = useState('');
    const [dimensionH, setDimensionH] = useState('');
    const [dimensionUnit, setDimensionUnit] = useState('cm');
    const [spoc, setSpoc] = useState('');
    const [sellerName, setSellerName] = useState('');
    const [sellerContactInformation, setSellerContactInformation] = useState('');
    const [itemsAvailable, setItemsAvailable] = useState('');
    const [error, setError] = useState('');

    // --- Variant Pricing State ---
    // variantMode: 'simple' | 'label' | 'combination'
    const [variantMode, setVariantMode] = useState('simple');
    const [variants, setVariants] = useState([]);
    const [comboSizes, setComboSizes] = useState('');
    const [comboColors, setComboColors] = useState('');
    // Which photo goes with each color — keyed by color name, independent of
    // `variants` (which only exist once sizes are typed too, so this can't live
    // on variant rows without the picker being unusable until sizes are filled).
    // { [colorName]: { colorImageAttachmentId, colorNewImageIndex } }
    const [colorImages, setColorImages] = useState({});
    // Ref lets the auto-generate effect read current variants without being a dependency (avoids infinite loop)
    const variantsRef = useRef([]);
    useEffect(() => { variantsRef.current = variants; });

    // --- Image State ---
    const [existingImages, setExistingImages] = useState([]);
    // Same photos as existingImages, paired with their stable attachment id —
    // needed to assign a specific photo to a variant color (URLs are presigned
    // and expire/regenerate, so they can't be used as a stored reference).
    const [existingImageAttachments, setExistingImageAttachments] = useState([]);
    const [newImages, setNewImages] = useState([]);
    // Local blob-URL previews of newImages, so a color can be assigned a photo
    // that hasn't been uploaded yet (revoked on change/unmount to avoid leaks).
    const [newImagePreviews, setNewImagePreviews] = useState([]);
    useEffect(() => {
        const urls = newImages.map(f => URL.createObjectURL(f));
        setNewImagePreviews(urls);
        return () => urls.forEach(u => URL.revokeObjectURL(u));
    }, [newImages]);

    // --- Category State ---
    const [allCategories, setAllCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newSubcategoryName, setNewSubcategoryName] = useState('');
    const [brands, setBrands] = useState([]);


    const resetForm = () => {
        setName('');
        setDescription('');
        setMrp('');
        setEyantraPurchasePrice('');
        setMarketOperatingPrice('');
        setBrand('');
        setGst('0');
        setColors('');
        setSizes('');
        setDimensionL('');
        setDimensionW('');
        setDimensionH('');
        setDimensionUnit('cm');
        setSpoc('');
        setSellerName('');
        setSellerContactInformation('');
        setSelectedCategory('');
        setSelectedSubcategory('');
        setNewCategoryName('');
        setNewSubcategoryName('');
        setItemsAvailable('');
        setExistingImages([]);
        setExistingImageAttachments([]);
        setNewImages([]);
        setError('');
        setVariantMode('simple');
        setVariants([]);
        setComboSizes('');
        setComboColors('');
        setColorImages({});
    };

    // Pre-fill form for editing or reset for creating
    useEffect(() => {
        if (productToEdit) {
            setName(productToEdit.name || '');
            setDescription(productToEdit.description || '');
            setMrp(productToEdit.mrp || '');
            setEyantraPurchasePrice(productToEdit.eyantraPurchasePrice || '');
            setMarketOperatingPrice(productToEdit.marketOperatingPrice || '');
            setBrand(productToEdit.brand || '');
            setGst(productToEdit.gst?.toString() || '0');
            setColors(productToEdit.colors || '');
            setSizes(productToEdit.sizes || '');
            setDimensionL(productToEdit.dimensionL || '');
            setDimensionW(productToEdit.dimensionW || '');
            setDimensionH(productToEdit.dimensionH || '');
            setDimensionUnit(productToEdit.dimensionUnit || 'cm');
            setSpoc(productToEdit.spoc || '');
            setSellerName(productToEdit.sellerName || '');
            setSellerContactInformation(productToEdit.sellerContactInformation || '');
            setSelectedCategory(productToEdit.category || '');
            setSelectedSubcategory(productToEdit.subcategory || '');
            setItemsAvailable(productToEdit.itemsAvailable || '');
            setExistingImages(productToEdit.imageUrls || []);
            setExistingImageAttachments(productToEdit.images || []);
            setNewImages([]);
            if (productToEdit.variants && productToEdit.variants.length > 0) {
                const isCombination = productToEdit.variants.some(v => v.color != null);
                setVariantMode(isCombination ? 'combination' : 'label');
                setVariants(productToEdit.variants.map(v => ({
                    label: v.label || '',
                    size: v.size || '',
                    color: v.color || '',
                    colorHex: v.colorHex || '',
                    available: v.available !== false,
                    mrp: v.mrp?.toString() || '',
                    eYantraPurchasePrice: v.eYantraPurchasePrice?.toString() || '',
                    sku: v.sku || '',
                    stockQty: v.stockQty != null ? String(v.stockQty) : '',
                    // What the server had when the form opened — see stockQtyPayload.
                    stockQtyOriginal: v.stockQty != null ? String(v.stockQty) : '',
                })));
                if (isCombination) {
                    const sz = [...new Set(productToEdit.variants.map(v => v.size).filter(Boolean))].join(', ');
                    const cl = [...new Map(productToEdit.variants.filter(v => v.color).map(v => [v.color, v.colorHex ? `${v.color}:${v.colorHex}` : v.color])).values()].join(', ');
                    setComboSizes(sz);
                    setComboColors(cl);
                    const images = {};
                    productToEdit.variants.filter(v => v.color).forEach(v => {
                        if (v.colorImageAttachmentId) images[v.color] = { colorImageAttachmentId: v.colorImageAttachmentId, colorNewImageIndex: null };
                    });
                    setColorImages(images);
                }
            } else {
                setVariantMode('simple');
                setVariants([]);
                setColorImages({});
            }
        } else {
            resetForm();
        }
    }, [productToEdit, isOpen]);

    // Fetch categories when the modal opens
    useEffect(() => {
        if (isOpen) {
            const fetchCategories = async () => {
                try {
                    const { data } = await api.get('/categories');
                    if (Array.isArray(data)) {
                        setAllCategories(data);
                    } else {
                        console.error("AddProductModal: API response for categories is not an array:", data);
                        setAllCategories([]);
                    }
                } catch (error) {
                    console.error("Failed to fetch categories", error);
                    setError("Could not load categories.");
                    setAllCategories([]);
                }
            };
            const fetchBrands = async () => {
                try {
                    const { data } = await api.get('/brands');
                    if (Array.isArray(data)) {
                        setBrands(data);
                    } else {
                        console.error("API response for brands is not an array:", data);
                        setBrands([]);
                    }
                } catch (error) {
                    setError(prev => prev ? prev + " & Could not load brands." : "Could not load brands.");
                    setBrands([]);
                }
            };
            fetchCategories();
            fetchBrands();
        }
    }, [isOpen]);

    // Auto-generate combination variants whenever sizes or colors change (no Generate button needed)
    useEffect(() => {
        if (variantMode !== 'combination') return;
        const sizeList = comboSizes.split(',').map(s => s.trim()).filter(Boolean);
        const colorList = comboColors.split(',').map(c => {
            const trimmed = c.trim();
            if (trimmed.startsWith('#')) return { name: trimmed, hex: trimmed };
            const [n, hex] = trimmed.split(':');
            return { name: n?.trim(), hex: (hex || '').trim() };
        }).filter(c => c.name);
        if (!colorList.length) { setVariants([]); return; }
        // Sizes are optional — a color-only product (e.g. a mug that just comes
        // in different colors) still needs one real variant row per color, or
        // there'd be nowhere to save its price/photo. null size = no size axis.
        const sizeAxis = sizeList.length ? sizeList : [null];
        const existing = new Map(variantsRef.current.map(v => [`${v.size}||${v.color}`, v]));
        const combos = colorList.flatMap(c => sizeAxis.map(s => {
            const resolvedHex = resolveColorHex(c.name, c.hex);
            const prev = existing.get(`${s}||${c.name}`);
            if (prev) return { ...prev };
            // New (size, color) combo — default its price to whatever's already
            // set for this size on another color, falling back to the master
            // Price field above if no sibling row has one yet either. Color
            // shouldn't change price, so this is just a starting point — still
            // editable per-row for the rare product where it genuinely does.
            const sameSizeRow = variantsRef.current.find(v => v.size === s && v.mrp);
            return {
                label: '', size: s, color: c.name, colorHex: resolvedHex, available: true,
                mrp: sameSizeRow?.mrp || mrp || '',
                eYantraPurchasePrice: sameSizeRow?.eYantraPurchasePrice || eyantraPurchasePrice || '',
                sku: '',
                stockQty: '',
            };
        }));
        setVariants(combos);
    }, [comboSizes, comboColors, variantMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // The master Price/Purchase Price fields seed every combination row — same
    // "linked until you override a specific cell" rule as size-to-size price
    // propagation below, just driven from this single field instead of the
    // first row typed. Tracks the previous master value so an edit only
    // overwrites rows still following along, not ones deliberately diverged.
    const prevMasterMrpRef = useRef(mrp);
    const prevMasterPurchaseRef = useRef(eyantraPurchasePrice);
    useEffect(() => {
        if (variantMode === 'combination') {
            const oldMrp = prevMasterMrpRef.current;
            setVariants(prev => prev.map(v => (v.mrp === '' || v.mrp === oldMrp) ? { ...v, mrp } : v));
        }
        prevMasterMrpRef.current = mrp;
    }, [mrp, variantMode]);
    useEffect(() => {
        if (variantMode === 'combination') {
            const oldPurchase = prevMasterPurchaseRef.current;
            setVariants(prev => prev.map(v => (v.eYantraPurchasePrice === '' || v.eYantraPurchasePrice === oldPurchase) ? { ...v, eYantraPurchasePrice: eyantraPurchasePrice } : v));
        }
        prevMasterPurchaseRef.current = eyantraPurchasePrice;
    }, [eyantraPurchasePrice, variantMode]);

    if (!isOpen) return null;

    // Rejects files over MAX_IMAGE_BYTES (the backend's per-file limit) with a message naming
    // them, rather than letting the whole save fail on the server.
    const withinSizeLimit = (files) => {
        const tooBig = files.filter(f => f.size > MAX_IMAGE_BYTES);
        if (tooBig.length) {
            setError(`Each image must be under 5 MB. Too large: ${tooBig.map(f => f.name).join(', ')}`);
        }
        return files.filter(f => f.size <= MAX_IMAGE_BYTES);
    };

    // Appends rather than replacing, so a photo already assigned to a color via
    // colorNewImageIndex keeps pointing at the right file if more are added later
    // (either from this general picker or a per-color upload tile below).
    const handleNewImageChange = (e) => {
        const files = withinSizeLimit(Array.from(e.target.files));
        setNewImages(prev => [...prev, ...files]);
        e.target.value = '';
    };

    // A color's own "+ upload" tile — stages the file the same way as the general
    // picker above, then immediately assigns it to this color so there's no
    // separate step of hunting for it afterwards.
    const handleColorImageUpload = (colorName, file) => {
        if (!file || !withinSizeLimit([file]).length) return;
        const newIndex = newImages.length;
        setNewImages(prev => [...prev, file]);
        setColorImages(prev => ({ ...prev, [colorName]: { colorImageAttachmentId: null, colorNewImageIndex: newIndex } }));
    };

    const handleDeleteImage = async (imageUrlToDelete) => {
        const ok = await confirm({
            title: 'Delete this image?',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            await api.delete(`/products/${productToEdit.id}/images`, { data: { imageUrl: imageUrlToDelete } });
            const deletedAttachment = existingImageAttachments.find(img => img.url === imageUrlToDelete);
            setExistingImages(existingImages.filter(img => img !== imageUrlToDelete));
            setExistingImageAttachments(existingImageAttachments.filter(img => img.url !== imageUrlToDelete));
            if (deletedAttachment) {
                setColorImages(prev => {
                    const next = { ...prev };
                    for (const [colorName, ci] of Object.entries(next)) {
                        if (ci.colorImageAttachmentId === deletedAttachment.id) next[colorName] = { colorImageAttachmentId: null, colorNewImageIndex: null };
                    }
                    return next;
                });
            }
        } catch (err) {
            setError('Failed to delete image. Please try again.');
            console.error(err);
        }
    };

    // Mirrors ProductService.validatePricesWithinMrp on the server: MOP and purchase price are
    // excl. GST while MRP includes it, so neither may exceed MRP once GST is added. Done in
    // whole paise so float error (100 * 1.18 = 118.00000000000001) can't reject an exact match.
    const findPriceAboveMrp = () => {
        const gstPct = parseInt(gst, 10) || 0;
        const check = (field, price, mrpValue) => {
            if (price === '' || price == null || mrpValue === '' || mrpValue == null) return null;
            const pricePaise = Math.round(parseFloat(price) * 100);
            const mrpPaise = Math.round(parseFloat(mrpValue) * 100);
            if (Number.isNaN(pricePaise) || Number.isNaN(mrpPaise)) return null;
            if (pricePaise * (100 + gstPct) <= mrpPaise * 100) return null;
            const withGst = (pricePaise * (100 + gstPct) / 10000).toFixed(2);
            const max = (Math.floor(mrpPaise * 100 / (100 + gstPct)) / 100).toFixed(2);
            return `${field} is excl. GST: ₹${price} + ${gstPct}% GST = ₹${withGst}, which exceeds MRP ₹${mrpValue}. Maximum for this MRP is ₹${max}.`;
        };
        const errors = [
            check('Market Operating Price', marketOperatingPrice, mrp),
            check('eYantra Purchase Price', eyantraPurchasePrice, mrp),
            ...(variantMode === 'simple' ? [] : variants.map((v, i) => check(
                `eYantra Purchase Price for variant '${v.label || [v.size, v.color].filter(Boolean).join(' / ') || `row ${i + 1}`}'`,
                v.eYantraPurchasePrice, v.mrp))),
        ];
        return errors.find(Boolean) || null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const finalCategory = selectedCategory === 'new' ? newCategoryName : selectedCategory;
        const finalSubcategory = selectedSubcategory === 'new' ? newSubcategoryName : selectedSubcategory;

        // Checked before anything is sent — the edit flow uploads new photos before saving.
        if (skuQtyMixed) {
            setError('Enter a Qty for every SKU (0 is allowed), or leave all SKU Qtys blank.');
            return;
        }
        if (!name || !mrp || !finalCategory || !finalSubcategory || (!skuTracked && itemsAvailable === '')) {
            setError('Please fill in all required fields.');
            return;
        }
        const priceError = findPriceAboveMrp();
        if (priceError) {
            setError(priceError);
            return;
        }

        try {
            const baseProductData = {
                name,
                description,
                mrp: parseFloat(mrp),
                eyantraPurchasePrice: eyantraPurchasePrice ? parseFloat(eyantraPurchasePrice) : null,
                marketOperatingPrice: marketOperatingPrice ? parseFloat(marketOperatingPrice) : null,
                brand,
                gst: parseInt(gst, 10),
                colors: variantMode === 'combination' ? null : colors,
                sizes: variantMode === 'simple' ? sizes : null,
                dimensionL: dimensionL ? parseFloat(dimensionL) : null,
                dimensionW: dimensionW ? parseFloat(dimensionW) : null,
                dimensionH: dimensionH ? parseFloat(dimensionH) : null,
                dimensionUnit,
                spoc,
                sellerName,
                sellerContactInformation,
                category: finalCategory,
                subcategory: finalSubcategory,
                // For a SKU-tracked product the server derives this from the SKU Qtys anyway.
                itemsAvailable: skuTracked ? skuQtyTotal : parseInt(itemsAvailable, 10),
            };

            if (productToEdit) {
                // Upload any newly picked photos FIRST — a color pointing at one
                // (colorNewImageIndex) needs the real attachment id that only
                // exists once the file has actually been uploaded, before we can
                // save the variants that reference it.
                let newIndexToAttachmentId = () => null;
                if (newImages.length > 0) {
                    const imageFormData = new FormData();
                    for (let i = 0; i < newImages.length; i++) {
                        imageFormData.append('images', newImages[i]);
                    }
                    const { data: updated } = await api.post(`/products/${productToEdit.id}/images`, imageFormData);
                    // New attachments land at the end, after the photos that already
                    // existed when this dialog opened.
                    const uploadedAttachments = (updated.images || []).slice(existingImageAttachments.length);
                    newIndexToAttachmentId = (idx) => uploadedAttachments[idx]?.id ?? null;
                }

                const productData = {
                    ...baseProductData,
                    variants: variantMode === 'simple' ? null : variants.map((v, i) => ({
                        label: v.label || null,
                        size: v.size || null,
                        color: v.color || null,
                        colorHex: v.colorHex || null,
                        available: v.available !== false,
                        mrp: parseFloat(v.mrp),
                        eYantraPurchasePrice: v.eYantraPurchasePrice ? parseFloat(v.eYantraPurchasePrice) : null,
                        sku: v.sku || null,
                        stockQty: stockQtyPayload(v),
                        sortOrder: i,
                        colorImageAttachmentId: colorImages[v.color]?.colorImageAttachmentId
                            ?? (colorImages[v.color]?.colorNewImageIndex != null ? newIndexToAttachmentId(colorImages[v.color].colorNewImageIndex) : null),
                    })),
                };
                await api.put(`/products/${productToEdit.id}`, productData);
            } else {
                if (newImages.length === 0) {
                    setError('Please select at least one image for a new product.');
                    return;
                }
                const productData = {
                    ...baseProductData,
                    variants: variantMode === 'simple' ? null : variants.map((v, i) => ({
                        label: v.label || null,
                        size: v.size || null,
                        color: v.color || null,
                        colorHex: v.colorHex || null,
                        available: v.available !== false,
                        mrp: parseFloat(v.mrp),
                        eYantraPurchasePrice: v.eYantraPurchasePrice ? parseFloat(v.eYantraPurchasePrice) : null,
                        sku: v.sku || null,
                        // Blank = this SKU isn't stock-tracked (limited by Items Available only).
                        stockQty: v.stockQty === '' ? null : parseInt(v.stockQty, 10),
                        sortOrder: i,
                        colorNewImageIndex: colorImages[v.color]?.colorNewImageIndex ?? null,
                    })),
                };
                const formData = new FormData();
                formData.append('product', new Blob([JSON.stringify(productData)], { type: 'application/json' }));
                for (let i = 0; i < newImages.length; i++) {
                    formData.append('images', newImages[i]);
                }
                await api.post('/products', formData);
                addToast(
                    user?.role !== 'ADMIN'
                        ? 'Product submitted — it appears on the store once an admin approves it.'
                        : 'Product added.',
                    'success'
                );
            }

            onProductAdded();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save product. Please try again.');
            console.error(err);
        }
    };

    // Only send a SKU's qty on update if the admin actually changed it; null tells the server to
    // keep its current count. Purchase entries raise these counts server-side, so resending the
    // value this form loaded would silently undo any purchase saved while the form was open.
    const stockQtyPayload = (v) => {
        if (v.stockQty === (v.stockQtyOriginal ?? '')) return null;
        return v.stockQty === '' ? 0 : parseInt(v.stockQty, 10);
    };
    const skuQtyTotal = variants.reduce((sum, v) => sum + (parseInt(v.stockQty, 10) || 0), 0);
    // Every SKU has a Qty → the product is SKU-tracked: Items Available is their sum, read-only here
    // and recomputed server-side on every save/order/purchase. Some-but-not-all is rejected — a
    // blank SKU would otherwise silently count as 0 and become unorderable.
    const hasSkuRows = variantMode !== 'simple' && variants.length > 0;
    const qtyFilled = (v) => v.stockQty !== '' && v.stockQty != null;
    const skuTracked = hasSkuRows && variants.every(qtyFilled);
    const skuQtyMixed = hasSkuRows && !skuTracked && variants.some(qtyFilled);

    const addVariantRow = () => setVariants(prev => [...prev, { label: '', size: '', color: '', colorHex: '', available: true, mrp: '', eYantraPurchasePrice: '', sku: '', stockQty: '' }]);
    const removeVariantRow = (i) => setVariants(prev => prev.filter((_, idx) => idx !== i));
    // Price fields propagate across colors for the same size — color shouldn't
    // change price, only size does, so typing a price once fills it in for
    // every other color at that size too (until someone deliberately edits a
    // specific cell to a different value, at which point that one row stops
    // following along). Combination mode only: label/simple rows share the
    // same blank size/color for every row, so this is a no-op there.
    const updateVariantRow = (i, field, value) => setVariants(prev => {
        const target = prev[i];
        const next = prev.map((v, idx) => idx === i ? { ...v, [field]: value } : v);
        if (variantMode === 'combination' && (field === 'mrp' || field === 'eYantraPurchasePrice') && target?.size) {
            const oldValue = target[field];
            return next.map(v =>
                (v.size === target.size && v.color !== target.color && v[field] === oldValue)
                    ? { ...v, [field]: value }
                    : v
            );
        }
        return next;
    });

    const availableSubcategories = allCategories.find(c => c.name === selectedCategory)?.subcategories || [];
    const gstSlabs = [0, 5, 12, 18, 28];
    const dimensionUnits = ['cm', 'm', 'inches', 'feet'];

    const modeCards = [
        { value: 'simple',      icon: '📦', title: 'Simple',        desc: 'One price. Sizes are just a label, no price difference.' },
        { value: 'label',       icon: '🏷️', title: 'Label Variants', desc: 'e.g. 500ml / 1L — each option has its own price.' },
        { value: 'combination', icon: '🎨', title: 'Size x Color',   desc: 'e.g. S + Red, M + Blue — each combo has its own price.' },
    ];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <h2>{productToEdit ? 'Edit Product' : 'Add New Product'}</h2>
                {productToEdit && productToEdit.approvalStatus === 'REJECTED' && (
                    <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #ef9a9a' }}>
                        <strong style={{ display: 'block', marginBottom: '5px' }}>Warning: Admin Feedback for Resubmission:</strong>
                        {productToEdit.adminFeedback}
                    </div>
                )}

                <div className="modal-body-scroll">
                    <form id="addProductForm" className="product-form-grid" onSubmit={handleSubmit}>

                        {/* Product Name */}
                        <div className="form-group full-width">
                            <label>Product Name</label>
                            <input type="text" value={name} onChange={e => setName(e.target.value)} required />
                        </div>

                        {/* Description */}
                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)} rows="3" />
                        </div>

                        {/* Category */}
                        <div className="form-group">
                            <label>Category</label>
                            <select value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); }} required>
                                <option value="" disabled>Select a category</option>
                                {allCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                                <option value="new">-- Add New Category --</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Subcategory</label>
                            <select value={selectedSubcategory} onChange={e => setSelectedSubcategory(e.target.value)} required disabled={!selectedCategory}>
                                <option value="" disabled>Select a subcategory</option>
                                {availableSubcategories.map(sub => <option key={sub.id} value={sub.name}>{sub.name}</option>)}
                                <option value="new">-- Add New Subcategory --</option>
                            </select>
                        </div>
                        {selectedCategory === 'new' && (
                            <div className="form-group full-width">
                                <label>New Category Name</label>
                                <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} required />
                            </div>
                        )}
                        {selectedSubcategory === 'new' && (
                            <div className="form-group full-width">
                                <label>New Subcategory Name</label>
                                <input type="text" value={newSubcategoryName} onChange={e => setNewSubcategoryName(e.target.value)} required />
                            </div>
                        )}

                        {/* Pricing basics */}
                        <div className="form-group">
                            <label>Price (MRP, incl. GST)</label>
                            <input type="number" value={mrp} onChange={e => setMrp(e.target.value)} required step="0.01" />
                        </div>
                        <div className="form-group">
                            <label>GST Slab</label>
                            <select value={gst} onChange={e => setGst(e.target.value)}>
                                {gstSlabs.map(slab => <option key={slab} value={slab}>GST {slab}%</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>eYantra Purchase Price</label>
                            <input type="number" value={eyantraPurchasePrice} onChange={e => setEyantraPurchasePrice(e.target.value)} step="0.01" placeholder="Optional" />
                        </div>
                        <div className="form-group">
                            <label>Market Operating Price (Excl. GST)</label>
                            <input type="number" value={marketOperatingPrice} onChange={e => setMarketOperatingPrice(e.target.value)} step="0.01" placeholder="Optional" />
                        </div>

                        {/* Brand + Stock */}
                        <div className="form-group">
                            <label>Brand</label>
                            <select value={brand} onChange={e => setBrand(e.target.value)} required>
                                <option value="" disabled>Select a brand</option>
                                {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Items Available</label>
                            {skuTracked ? (
                                <input type="number" value={skuQtyTotal} readOnly
                                    title="Sum of the SKU Qtys below"
                                    style={{ background: '#F3F4F6', color: '#374151', cursor: 'not-allowed' }} />
                            ) : (
                                <input type="number" value={itemsAvailable} onChange={e => setItemsAvailable(e.target.value)} required placeholder="e.g., 50" />
                            )}
                            {skuTracked && (
                                <small style={{ display: 'block', color: '#6B7280', marginTop: 4 }}>
                                    Sum of SKU quantities — edit the Qty per SKU below. Orders, cancellations and Brandstore purchase entries keep it up to date.
                                </small>
                            )}
                            {skuQtyMixed && (
                                <small style={{ display: 'block', color: '#B45309', marginTop: 4 }}>
                                    Some SKUs have a Qty and some don't. Fill in every SKU's Qty (0 is allowed) to track stock per SKU, or clear them all to enter Items Available by hand.
                                </small>
                            )}
                            {hasSkuRows && !skuTracked && !skuQtyMixed && (
                                <small style={{ display: 'block', color: '#6B7280', marginTop: 4 }}>
                                    Entered by hand. Fill in a Qty for every SKU below to have this calculated per size/colour instead.
                                </small>
                            )}
                        </div>

                        {/* Colors (hidden in combination mode — colors come from variant rows) */}
                        {variantMode !== 'combination' && (
                            <div className="form-group">
                                <label>Colors</label>
                                <input type="text" value={colors} onChange={e => setColors(e.target.value)} placeholder="e.g., Red, #00FF00, Blue" />
                            </div>
                        )}

                        {/* ── Variant / Pricing Mode ─────────────────────────── */}
                        <div className="form-group full-width">
                            <label>Pricing &amp; Variants</label>

                            {/* Mode cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                                {modeCards.map(m => (
                                    <div
                                        key={m.value}
                                        onClick={() => { setVariantMode(m.value); setVariants([]); setComboSizes(''); setComboColors(''); }}
                                        style={{
                                            border: `2px solid ${variantMode === m.value ? '#1a73e8' : '#e0e0e0'}`,
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            cursor: 'pointer',
                                            background: variantMode === m.value ? '#e8f0fe' : '#fafafa',
                                            userSelect: 'none',
                                            transition: 'border-color 0.15s, background 0.15s',
                                        }}
                                    >
                                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>{m.icon}</div>
                                        <div style={{ fontWeight: 700, fontSize: '13px', color: variantMode === m.value ? '#1a73e8' : '#333', marginBottom: '3px' }}>{m.title}</div>
                                        <div style={{ fontSize: '11px', color: '#888', lineHeight: 1.4 }}>{m.desc}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Simple mode — optional sizes text field */}
                            {variantMode === 'simple' && (
                                <div>
                                    <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '4px' }}>Sizes (optional, comma-separated)</label>
                                    <input type="text" value={sizes} onChange={e => setSizes(e.target.value)} placeholder="e.g., S, M, L, XL" style={{ width: '100%', boxSizing: 'border-box' }} />
                                </div>
                            )}

                            {/* Label variants — flat table */}
                            {variantMode === 'label' && (
                                <div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ background: '#f5f5f5' }}>
                                                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>Label (e.g. 500ml)</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>MRP (Rs.)</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>Purchase Price (Rs.)</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>SKU</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd', width: '80px' }}>Qty</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #ddd', width: '30px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {variants.map((v, i) => (
                                                <tr key={i}>
                                                    <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                                        <input type="text" value={v.label} onChange={e => updateVariantRow(i, 'label', e.target.value)} placeholder="500ml" style={{ width: '100%', boxSizing: 'border-box' }} required />
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                                        <input type="number" value={v.mrp} onChange={e => updateVariantRow(i, 'mrp', e.target.value)} placeholder="299" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} required />
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                                        <input type="number" value={v.eYantraPurchasePrice} onChange={e => updateVariantRow(i, 'eYantraPurchasePrice', e.target.value)} placeholder="Optional" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} />
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                                        <input type="text" value={v.sku || ''} onChange={e => updateVariantRow(i, 'sku', e.target.value)} placeholder="Optional" style={{ width: '100%', boxSizing: 'border-box' }} />
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                                        <input type="number" min="0" step="1" value={v.stockQty ?? ''} onChange={e => updateVariantRow(i, 'stockQty', e.target.value)} placeholder="Not tracked" style={{ width: '100%', boxSizing: 'border-box' }} />
                                                    </td>
                                                    <td style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        <button type="button" onClick={() => removeVariantRow(i)} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '16px' }}>&#x2715;</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <button type="button" onClick={addVariantRow} style={{ marginTop: '8px', padding: '6px 14px', background: '#f0f0f0', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>+ Add Variant</button>
                                </div>
                            )}

                            {/* Combination variants — live-reactive, no Generate button */}
                            {variantMode === 'combination' && (
                                <div>
                                    {/* Inputs side by side; chips appear below as the user types */}
                                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '4px' }}>
                                                Option 1 — Size / Volume / etc. <span style={{ color: '#999', fontWeight: 400 }}>(comma-separated)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={comboSizes}
                                                onChange={e => setComboSizes(e.target.value)}
                                                placeholder="e.g. S, M, L  or  500ml, 1L"
                                                style={{ width: '100%', boxSizing: 'border-box' }}
                                            />
                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>
                                                Comma separates options. For a combined label use / or - (e.g. <strong>S/100ML</strong>)
                                            </div>
                                            {comboSizes.trim() && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                                    {comboSizes.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
                                                        <span key={i} style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: 600 }}>{s}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '4px' }}>
                                                Colors <span style={{ color: '#999', fontWeight: 400 }}>(name or name:#hex, comma-separated)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={comboColors}
                                                onChange={e => setComboColors(e.target.value)}
                                                placeholder="Red:#FF0000, Blue:#0000FF"
                                                style={{ width: '100%', boxSizing: 'border-box' }}
                                            />
                                            {comboColors.trim() && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                                    {comboColors.split(',').map(c => {
                                                        const trimmed = c.trim();
                                                        if (trimmed.startsWith('#')) return { name: trimmed, hex: trimmed };
                                                        const [n, h] = trimmed.split(':');
                                                        return { name: n?.trim(), hex: h?.trim() };
                                                    }).filter(c => c.name).map((c, i) => (
                                                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#f0f0f0', borderRadius: '12px', padding: '2px 10px', fontSize: '12px' }}>
                                                            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: resolveColorHex(c.name, c.hex), display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                                                            {c.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Photo per color — usable as soon as colors are typed, independent of
                                        sizes (the pricing table below needs both, but a photo only needs a
                                        color to attach to). Selecting this color on the storefront swaps
                                        the main image to whatever's picked here. */}
                                    {(() => {
                                        const parsedComboColors = comboColors.split(',').map(c => {
                                            const trimmed = c.trim();
                                            if (trimmed.startsWith('#')) return { name: trimmed, hex: trimmed };
                                            const [n, h] = trimmed.split(':');
                                            return { name: n?.trim(), hex: h?.trim() };
                                        }).filter(c => c.name);
                                        if (parsedComboColors.length === 0) return null;
                                        return (
                                            <div style={{ marginBottom: '14px' }}>
                                                <div style={{ fontSize: '12px', color: '#555', fontWeight: 600, marginBottom: '8px' }}>
                                                    Photo per color
                                                </div>
                                                {parsedComboColors.map(c => (
                                                    <ColorPhotoPicker
                                                        key={c.name}
                                                        colorName={c.name}
                                                        colorHex={resolveColorHex(c.name, c.hex)}
                                                        assignment={colorImages[c.name]}
                                                        existingImageAttachments={existingImageAttachments}
                                                        newImagePreviews={newImagePreviews}
                                                        onUpload={file => handleColorImageUpload(c.name, file)}
                                                        onSelectExisting={attachmentId => setColorImages(prev => ({ ...prev, [c.name]: { colorImageAttachmentId: attachmentId, colorNewImageIndex: null } }))}
                                                        onSelectPending={index => setColorImages(prev => ({ ...prev, [c.name]: { colorImageAttachmentId: null, colorNewImageIndex: index } }))}
                                                        onClear={() => setColorImages(prev => ({ ...prev, [c.name]: { colorImageAttachmentId: null, colorNewImageIndex: null } }))}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })()}

                                    {/* Grouped by color — each color is its own visible section */}
                                    {variants.length > 0 ? (
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#555', fontWeight: 600, marginBottom: '2px' }}>
                                                {variants.length} combination{variants.length !== 1 ? 's' : ''} — set the price for each
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#999', marginBottom: '10px' }}>
The master Price field above fills in every row here. Type a size's price once and it applies to every color at that size too. Edit a specific cell to override just that one.
                                            </div>
                                            {(() => { const hasSizes = comboSizes.trim().length > 0; return [...new Map(variants.map(v => [v.color, v.colorHex])).entries()].map(([colorName, colorHex]) => {
                                                return (
                                                <div key={colorName} style={{ marginBottom: '14px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                                                    {/* Color section header */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                                                        <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: colorHex || '#888', display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                                                        <span style={{ fontWeight: 700, fontSize: '14px', color: '#222', flex: 1 }}>{colorName}</span>
                                                        <input
                                                            type="color"
                                                            value={colorHex || '#888888'}
                                                            onChange={e => setVariants(prev => prev.map(v => v.color === colorName ? { ...v, colorHex: e.target.value } : v))}
                                                            title="Adjust hex"
                                                            style={{ width: '22px', height: '22px', border: 'none', cursor: 'pointer', padding: 0 }}
                                                        />
                                                        <span style={{ fontSize: '11px', color: '#999' }}>
                                                            {hasSizes ? `${variants.filter(v => v.color === colorName).length} sizes` : 'no size options'}
                                                        </span>
                                                    </div>
                                                    {/* Rows for this color */}
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                        <thead>
                                                            <tr style={{ background: '#fafafa' }}>
                                                                {hasSizes && <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', width: '80px', color: '#666', fontWeight: 600 }}>Size</th>}
                                                                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#666', fontWeight: 600 }}>MRP (Rs.)</th>
                                                                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#666', fontWeight: 600 }}>Purchase Price (Rs.)</th>
                                                                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#666', fontWeight: 600 }}>SKU</th>
                                                                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', width: '80px', color: '#666', fontWeight: 600 }}>Qty</th>
                                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: '70px', color: '#666', fontWeight: 600 }}>In Stock</th>
                                                                <th style={{ borderBottom: '1px solid #e0e0e0', width: '32px' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {variants.map((v, i) => v.color !== colorName ? null : (
                                                                <tr key={v.size || 'no-size'} style={{ background: v.available === false ? '#fff5f5' : 'transparent' }}>
                                                                    {hasSizes && <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 700 }}>{v.size}</td>}
                                                                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                                                                        <input type="number" value={v.mrp} onChange={e => updateVariantRow(i, 'mrp', e.target.value)} placeholder="299" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} required />
                                                                    </td>
                                                                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                                                                        <input type="number" value={v.eYantraPurchasePrice} onChange={e => updateVariantRow(i, 'eYantraPurchasePrice', e.target.value)} placeholder="Optional" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} />
                                                                    </td>
                                                                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                                                                        <input type="text" value={v.sku || ''} onChange={e => updateVariantRow(i, 'sku', e.target.value)} placeholder="Optional" style={{ width: '100%', boxSizing: 'border-box' }} />
                                                                    </td>
                                                                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                                                                        <input type="number" min="0" step="1" value={v.stockQty ?? ''} onChange={e => updateVariantRow(i, 'stockQty', e.target.value)} placeholder="Not tracked" style={{ width: '100%', boxSizing: 'border-box' }} />
                                                                    </td>
                                                                    <td style={{ padding: '4px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                                                        <input type="checkbox" checked={v.available !== false} onChange={e => updateVariantRow(i, 'available', e.target.checked)} />
                                                                    </td>
                                                                    <td style={{ padding: '4px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                                                        <button type="button" onClick={() => removeVariantRow(i)} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '15px' }}>&#x2715;</button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                );
                                            }); })()}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#aaa', fontSize: '13px', background: '#fafafa', borderRadius: '6px', border: '1px dashed #ddd' }}>
                                            Type sizes and colors above — the pricing table appears automatically
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Dimensions */}
                        <div className="form-group full-width">
                            <label>Dimensions</label>
                            <div className="dimension-group">
                                <input type="number" value={dimensionL} onChange={e => setDimensionL(e.target.value)} placeholder="L" />
                                <span>x</span>
                                <input type="number" value={dimensionW} onChange={e => setDimensionW(e.target.value)} placeholder="W" />
                                <span>x</span>
                                <input type="number" value={dimensionH} onChange={e => setDimensionH(e.target.value)} placeholder="H" />
                                <select value={dimensionUnit} onChange={e => setDimensionUnit(e.target.value)}>
                                    {dimensionUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Seller info */}
                        <div className="form-group">
                            <label>SPOC</label>
                            <input type="text" value={spoc} onChange={e => setSpoc(e.target.value)} placeholder="e.g., John Doe" />
                        </div>
                        <div className="form-group">
                            <label>Seller Name</label>
                            <input type="text" value={sellerName} onChange={e => setSellerName(e.target.value)} placeholder="e.g., Global Electronics" />
                        </div>
                        <div className="form-group full-width">
                            <label>Seller Contact Information</label>
                            <input type="text" value={sellerContactInformation} onChange={e => setSellerContactInformation(e.target.value)} placeholder="e.g., 9876543210" />
                        </div>

                        {/* Images */}
                        {productToEdit && existingImages.length > 0 && (
                            <div className="form-group full-width">
                                <label>Existing Images</label>
                                <div className="image-preview-grid">
                                    {existingImages.map((imageUrl, index) => (
                                        <div key={index} className="image-preview-item">
                                            <img src={imageUrl} alt={`Product image ${index + 1}`} />
                                            <button type="button" onClick={() => handleDeleteImage(imageUrl)} className="delete-image-btn">&#x2715;</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="form-group full-width">
                            <label>{productToEdit ? 'Upload New Images' : 'Product Images'}</label>
                            <input type="file" accept="image/*" onChange={handleNewImageChange} multiple />
                        </div>

                    </form>
                </div>

                {error && <p className="error-message">{error}</p>}
                <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                    <button type="submit" form="addProductForm" className="btn-submit">
                        {productToEdit ? 'Save Changes' : 'Add Product'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddProductModal;